// ── BUDGETS PAGE ─────────────────────────────────────────────
// One nav item, one tab per job:
//   Build a Budget · Spend Plan · CDC Spend Plan · Level of Effort · Your Documents
// (CDC Spend Plan and Level of Effort live in their own files.)
//
// Written for a grants officer, not an engineer: no jargon on screen ("Your
// Documents", not "reference library"), every number visible and editable,
// and each screen says what it will do before you commit to it.
import { useState, useEffect, useCallback, useRef } from "react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import { API, apiFetch } from "../utils/api";
import { parsePDF, parseDocx, parseExcel } from "../utils/grants";
import CdcSpendPlanTab from "./CdcSpendPlanTab";
import BudgetDocuments from "./BudgetDocuments";
import LoeTab from "./LoeTab";

const TABS = [
  { id: "build",   label: "Build a Budget" },
  { id: "plan",    label: "Spend Plan" },
  { id: "cdc",     label: "CDC Spend Plan" },
  { id: "loe",     label: "Level of Effort" },
  { id: "library", label: "Your Documents" },
];

const money = (n, cur) => {
  const v = Number(n || 0);
  const sym = { USD: "$", NGN: "₦", EUR: "€", GBP: "£" }[cur] || "";
  return sym + v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Recomputed in the browser so edits update instantly; the server recomputes
// authoritatively on save. Same formula both sides: qty x cost x months.
const lineTotal = (l) => (Number(l.quantity) || 0) * (Number(l.unitCost) || 0) * (Number(l.months) || 1);

export default function BudgetPage({ currentGrantData, orgProfile, T: _T }) {
  const T = useT();
  const [tab, setTab] = useState("build");
  // Budgets built on the Build tab can be pushed straight into Spend Plan
  // without exporting and re-uploading them — that round trip was the most
  // obvious friction in using the two together.
  const [planBudgets, setPlanBudgets] = useState([]);
  const sendToPlan = (entry) => { setPlanBudgets(b => [...b, entry]); setTab("plan"); };

  const card   = { background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20 };
  const label  = { fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6, display: "block" };
  const input  = { width: "100%", padding: "9px 11px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textBody, fontSize: 13, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" };
  const btn    = (primary) => ({ padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer", minHeight: 38, border: `1px solid ${primary ? TOKENS.gold : T.border}`, background: primary ? TOKENS.gold : "transparent", color: primary ? "#fff" : T.textBody });

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "22px 24px 60px", background: T.bgApp }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h1 style={{ fontFamily: FONT_H, fontSize: 24, fontWeight: 700, color: T.text, margin: "0 0 4px" }}>Budgets</h1>
        <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted, margin: "0 0 20px" }}>
          Build a budget for a grant, turn your budgets into a monthly spend plan, compile the CDC spend plan, compare staff Level of Effort, and teach NofoDesk your writing style.
        </p>

        <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.border}`, marginBottom: 22, overflowX: "auto" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "10px 16px", background: "none", border: "none", cursor: "pointer",
              fontFamily: FONT_UI, fontSize: 13.5, fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? T.text : T.textMuted,
              borderBottom: `2px solid ${tab === t.id ? TOKENS.gold : "transparent"}`, marginBottom: -1,
            }}>{t.label}</button>
          ))}
        </div>

        {tab === "build"   && <BuildBudget   {...{ T, card, label, input, btn, money, currentGrantData, orgProfile, sendToPlan }} />}
        {tab === "plan"    && <SpendPlan     {...{ T, card, label, input, btn, money, planBudgets, setPlanBudgets }} />}
        {tab === "cdc"     && <CdcSpendPlanTab {...{ T, card, label, input, btn }} />}
        {tab === "loe"     && <LoeTab        {...{ T, card, label, btn }} />}
        {tab === "library" && <YourDocuments {...{ T, card, label, input, btn }} />}
      </div>
    </div>
  );
}

// ── TAB 1 — BUILD A BUDGET ───────────────────────────────────
function BuildBudget({ T, card, label, input, btn, money, currentGrantData, orgProfile, sendToPlan }) {
  // Two kinds of budget. Grant budgets follow a funder's published cost rules;
  // internal budgets are the organisation's own planning and have no funder.
  const [mode, setMode]       = useState(currentGrantData ? "grant" : "internal");
  const [purpose, setPurpose] = useState("");
  const [dept, setDept]       = useState("");
  const [months, setMonths]   = useState(12);
  const [target, setTarget]   = useState("");
  const [notes, setNotes]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState("");
  const [result, setResult]   = useState(null);

  const grant = currentGrantData?.opportunityIdentification || {};
  const fp    = currentGrantData?.fundingParameters || {};

  useEffect(() => {
    if (fp.awardMax && !target) setTarget(String(fp.awardMax));
    const d = currentGrantData?.timeline?.projectDurationMonths;
    if (d && Number(d)) setMonths(Number(d));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGrantData]);

  const generate = async () => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`${API}/budget/draft`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, purpose, department: dept, grantData: mode === "grant" ? currentGrantData : null, orgProfile: orgProfile || {}, targetAmount: target, months, notes }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Could not build the budget");
      // A new draft starts the justification and downloads afresh.
      setResult({ ...json, draftId: Date.now() });
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  // Editing a cell recomputes that row and every total, immediately.
  const edit = (i, field, value) => {
    setResult(r => {
      const lines = r.budget.lines.map((l, n) => n === i ? { ...l, [field]: value } : l);
      const withTotals = lines.map(l => ({ ...l, total: lineTotal(l) }));
      const direct = withTotals.reduce((s, l) => s + l.total, 0);
      const ind = direct * ((r.budget.indirect.appliedRatePct || 0) / 100);
      return { ...r, budget: { ...r.budget, lines: withTotals, directTotal: direct, indirect: { ...r.budget.indirect, amount: ind }, grandTotal: direct + ind } };
    });
  };
  const removeLine = (i) => setResult(r => {
    const lines = r.budget.lines.filter((_, n) => n !== i);
    const direct = lines.reduce((s, l) => s + l.total, 0);
    const ind = direct * ((r.budget.indirect.appliedRatePct || 0) / 100);
    return { ...r, budget: { ...r.budget, lines, directTotal: direct, indirect: { ...r.budget.indirect, amount: ind }, grandTotal: direct + ind } };
  });
  // Justifications arrive for many lines at once; they never touch amounts.
  const applyJustifications = (byIndex) => setResult(r => ({
    ...r, budget: { ...r.budget, lines: r.budget.lines.map((l, i) => (byIndex[i] != null ? { ...l, justification: byIndex[i] } : l)) },
  }));

  const b = result?.budget;
  const cur = b?.currency || "USD";
  const budgetTitle = (mode === "internal" ? (purpose || "Internal budget") : (grant.callTitle || "Grant budget")).slice(0, 60);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={card}>
        <span style={label}>What is this budget for?</span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
          {[
            { id: "internal", title: "Internal budget",     sub: "For your own planning — a department, a programme, an activity. No funder rules applied." },
            { id: "grant",    title: "A grant application", sub: currentGrantData ? "Follows this funder's allowable costs and indirect cap." : "Extract a grant first to use this." },
          ].map(o => {
            const disabled = o.id === "grant" && !currentGrantData;
            const on = mode === o.id;
            return (
              <button key={o.id} onClick={() => !disabled && setMode(o.id)} disabled={disabled}
                style={{
                  flex: "1 1 240px", textAlign: "left", padding: "12px 14px", borderRadius: 10,
                  border: `1px solid ${on ? TOKENS.gold : T.border}`,
                  background: on ? "rgba(245,166,35,0.08)" : "transparent",
                  cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
                }}>
                <div style={{ fontFamily: FONT_UI, fontSize: 13.5, fontWeight: 600, color: T.text, marginBottom: 3 }}>{o.title}</div>
                <div style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>{o.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={card}>
        {mode === "internal" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 14 }}>
            <div><span style={label}>What's it for?</span><input style={input} value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="e.g. Q1 community outreach" /></div>
            <div><span style={label}>Department or unit</span><input style={input} value={dept} onChange={e => setDept(e.target.value)} placeholder="e.g. Programmes — Kano" /></div>
          </div>
        )}
        {mode === "grant" && currentGrantData && (
          <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${T.borderDim}` }}>
            <span style={label}>Building a budget for</span>
            <div style={{ fontFamily: FONT_UI, fontSize: 15, fontWeight: 600, color: T.text }}>{grant.callTitle || "Loaded grant"}</div>
            <div style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, marginTop: 2 }}>
              {grant.funderName || "Unknown funder"}
              {fp.awardMax ? ` · ceiling ${fp.awardMax}` : ""}
              {fp.indirectCostCap ? ` · indirect cap ${fp.indirectCostCap}` : ""}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 14 }}>
          <div><span style={label}>{mode === "grant" ? "Total you're asking for" : "Total available (optional)"}</span><input style={input} value={target} onChange={e => setTarget(e.target.value)} placeholder="e.g. 135,000" /></div>
          <div><span style={label}>How many months</span><input style={input} type="number" min="1" max="60" value={months} onChange={e => setMonths(e.target.value)} /></div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <span style={label}>Anything specific to include? (optional)</span>
          <textarea style={{ ...input, minHeight: 68, resize: "vertical" }} value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="e.g. two field officers in Kano, one vehicle, quarterly monitoring visits" />
        </div>
        <button onClick={generate} disabled={busy || (mode === "grant" && !currentGrantData)} style={{ ...btn(true), opacity: busy || (mode === "grant" && !currentGrantData) ? 0.5 : 1, cursor: busy || (mode === "grant" && !currentGrantData) ? "not-allowed" : "pointer" }}>
          {busy ? "Building…" : "Build the budget"}
        </button>
        {error && <div style={{ marginTop: 12, fontFamily: FONT_UI, fontSize: 13, color: TOKENS.statusRed }}>{error}</div>}
      </div>

      {b && (
        <div style={card}>
          {result.groundedOn?.length > 0 && (
            <div style={{ marginBottom: 14, padding: "10px 12px", background: T.bgApp, border: `1px solid ${T.borderDim}`, borderRadius: 8, fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted }}>
              Based on your past work: {result.groundedOn.map(d => d.title).join(", ")}
            </div>
          )}
          {b.warnings?.map(w => (
            <div key={w} style={{ marginBottom: 12, padding: "10px 12px", background: "rgba(245,166,35,0.08)", border: `1px solid ${TOKENS.gold}55`, borderRadius: 8, fontFamily: FONT_UI, fontSize: 12.5, color: T.textBody }}>⚠ {w}</div>
          ))}

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
              <thead>
                <tr>
                  {["Category", "What it's for", "Qty", "Unit cost", "Months", "Total", ""].map(h => (
                    <th key={h} style={{ textAlign: h === "Total" ? "right" : "left", fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textMuted, padding: "8px 8px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.lines.map((l, i) => (
                  <tr key={i}>
                    <td style={{ padding: "6px 8px", fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, whiteSpace: "nowrap" }}>{l.category}</td>
                    <td style={{ padding: "6px 8px", minWidth: 240 }}>
                      <input style={{ ...input, padding: "6px 8px", fontSize: 12.5 }} value={l.description} onChange={e => edit(i, "description", e.target.value)} title={l.justification} />
                    </td>
                    {["quantity", "unitCost", "months"].map(f => (
                      <td key={f} style={{ padding: "6px 4px", width: 92 }}>
                        <input style={{ ...input, padding: "6px 8px", fontSize: 12.5, textAlign: "right" }} value={l[f]} onChange={e => edit(i, f, e.target.value)} />
                      </td>
                    ))}
                    <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 12.5, color: T.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{money(l.total, cur)}</td>
                    <td style={{ padding: "6px 4px" }}>
                      <button onClick={() => removeLine(i)} aria-label="Remove line" style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 15, padding: 3 }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {[
                  ["Direct costs", b.directTotal],
                  [`Indirect (${b.indirect.appliedRatePct}%)`, b.indirect.amount],
                  ["Total", b.grandTotal],
                ].map(([lbl, amt], n) => (
                  <tr key={lbl}>
                    <td colSpan={5} style={{ padding: "9px 8px", textAlign: "right", fontFamily: FONT_UI, fontSize: 13, fontWeight: n === 2 ? 700 : 500, color: n === 2 ? T.text : T.textMuted, borderTop: n === 0 ? `1px solid ${T.border}` : "none" }}>{lbl}</td>
                    <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: FONT_MONO, fontSize: n === 2 ? 15 : 13, fontWeight: n === 2 ? 700 : 500, color: n === 2 ? TOKENS.gold : T.textBody, fontVariantNumeric: "tabular-nums", borderTop: n === 0 ? `1px solid ${T.border}` : "none", whiteSpace: "nowrap" }}>{money(amt, cur)}</td>
                    <td style={{ borderTop: n === 0 ? `1px solid ${T.border}` : "none" }} />
                  </tr>
                ))}
              </tfoot>
            </table>
          </div>

          <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={() => sendToPlan({
              name: (mode === "internal" ? (purpose || "Internal budget") : (grant.callTitle || "Grant budget")).slice(0, 60),
              department: dept, location: "", budget: b,
            })} style={btn(false)}>Use this in a Spend Plan →</button>
          </div>

          {result.awardCeiling && b.grandTotal > 0 && (
            <div style={{ marginTop: 12, fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted }}>
              Funder ceiling: {result.awardCeiling}
            </div>
          )}

          {result.assumptions?.length > 0 && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.borderDim}` }}>
              <span style={label}>Assumptions to check</span>
              <ul style={{ margin: 0, paddingLeft: 18, fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, lineHeight: 1.7 }}>
                {result.assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {b && (
        <BudgetDocuments key={result.draftId} {...{ T, card, label, input, btn, money }} budget={b} onEdit={edit} onJustified={applyJustifications}
          context={{
            mode, purpose, months, title: budgetTitle,
            grantData: mode === "grant" ? currentGrantData : null, orgProfile: orgProfile || {},
            organisation: orgProfile?.name || orgProfile?.orgName || "", funder: mode === "grant" ? (grant.funderName || "") : "",
            assumptions: result.assumptions || [],
          }} />
      )}
    </div>
  );
}

// ── TAB 2 — SPEND PLAN ───────────────────────────────────────
function SpendPlan({ T, card, label, input, btn, money, planBudgets, setPlanBudgets }) {
  // Budgets arrive either from an upload here, or handed over from the Build
  // tab — same shape, so the rest of this screen does not care which.
  const budgets = planBudgets;
  const setBudgets = setPlanBudgets;
  const [months, setMonths]   = useState(12);
  const [start, setStart]     = useState("");
  const [profile, setProfile] = useState("even");
  const [plan, setPlan]       = useState(null);
  const [busy, setBusy]       = useState("");
  const [error, setError]     = useState("");
  const fileRef = useRef(null);

  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    for (const file of files) {
      setBusy(`Reading ${file.name}…`); setError("");
      try {
        const name = file.name.toLowerCase();
        let text = "";
        if (name.endsWith(".xlsx") || name.endsWith(".xls")) text = await parseExcel(file);
        else if (name.endsWith(".pdf"))  text = await parsePDF(file);
        else if (name.endsWith(".docx")) text = await parseDocx(file);
        else { setError(`${file.name}: use Excel, PDF or Word`); continue; }

        const res = await apiFetch(`${API}/budget/normalise`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rows: text, name: file.name.replace(/\.[^.]+$/, ""), currency: "USD" }),
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error || "Could not read that file");
        setBudgets(b => [...b, { name: json.name, department: "", location: "", budget: json.budget }]);
      } catch (err) { setError(`${file.name}: ${err.message}`); }
    }
    setBusy("");
  };

  const build = async () => {
    setBusy("Building plan…"); setError(""); setPlan(null);
    try {
      const res = await apiFetch(`${API}/budget/spend-plan`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          budgets: budgets.map(b => ({ name: b.name, department: b.department, location: b.location, lines: b.budget.lines })),
          months, startDate: start || null, profile, currency: budgets[0]?.budget?.currency || "USD",
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Could not build the plan");
      setPlan(json.plan);
    } catch (e) { setError(e.message); }
    setBusy("");
  };

  const cur = plan?.currency || "USD";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={card}>
        <div style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textBody, lineHeight: 1.65, marginBottom: 14 }}>
          Upload one budget per department, state or site. NofoDesk reads each one, then spreads the costs across the months of your project so you can see what you'll spend and when.
        </div>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.pdf,.docx" multiple onChange={onFiles} style={{ display: "none" }} />
        <button onClick={() => fileRef.current?.click()} style={btn(false)}>＋ Add budget files</button>
        {busy && <span style={{ marginLeft: 12, fontFamily: FONT_UI, fontSize: 13, color: T.textMuted }}>{busy}</span>}
        {error && <div style={{ marginTop: 10, fontFamily: FONT_UI, fontSize: 13, color: TOKENS.statusRed }}>{error}</div>}

        {budgets.length > 0 && (
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            {budgets.map((b, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 150px 150px 90px 30px", gap: 8, alignItems: "center" }}>
                <div style={{ fontFamily: FONT_UI, fontSize: 13, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</div>
                <input style={{ ...input, padding: "6px 9px", fontSize: 12.5 }} placeholder="Department" value={b.department}
                  onChange={e => setBudgets(x => x.map((v, n) => n === i ? { ...v, department: e.target.value } : v))} />
                <input style={{ ...input, padding: "6px 9px", fontSize: 12.5 }} placeholder="State / site" value={b.location}
                  onChange={e => setBudgets(x => x.map((v, n) => n === i ? { ...v, location: e.target.value } : v))} />
                <div style={{ fontFamily: FONT_MONO, fontSize: 12.5, color: T.textMuted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(b.budget.grandTotal, b.budget.currency)}</div>
                <button onClick={() => setBudgets(x => x.filter((_, n) => n !== i))} aria-label="Remove" style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 15 }}>×</button>
              </div>
            ))}
          </div>
        )}

        {budgets.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${T.borderDim}`, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, alignItems: "end" }}>
            <div><span style={label}>Months</span><input style={input} type="number" min="1" max="60" value={months} onChange={e => setMonths(e.target.value)} /></div>
            <div><span style={label}>Start month</span><input style={input} type="date" value={start} onChange={e => setStart(e.target.value)} /></div>
            <div>
              <span style={label}>Spending pattern</span>
              <select style={input} value={profile} onChange={e => setProfile(e.target.value)}>
                <option value="even">Even across the period</option>
                <option value="frontload">More at the start</option>
                <option value="backload">More at the end</option>
              </select>
            </div>
            <button onClick={build} style={btn(true)}>Build spend plan</button>
          </div>
        )}
      </div>

      {plan && (
        <div style={card}>
          {!plan.reconciles && (
            <div style={{ marginBottom: 12, padding: "10px 12px", background: "rgba(232,69,69,0.08)", border: `1px solid ${TOKENS.statusRed}55`, borderRadius: 8, fontFamily: FONT_UI, fontSize: 12.5, color: T.textBody }}>
              ⚠ This plan does not add up to the budget total. Do not submit it — please re-check the uploaded files.
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", fontFamily: FONT_MONO, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: T.textMuted, borderBottom: `1px solid ${T.border}`, position: "sticky", left: 0, background: T.bgSurface }}>Budget</th>
                  {plan.columns.map(c => (
                    <th key={c.key} style={{ textAlign: "right", padding: "8px", fontFamily: FONT_MONO, fontSize: 10, color: T.textMuted, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{c.label}</th>
                  ))}
                  <th style={{ textAlign: "right", padding: "8px", fontFamily: FONT_MONO, fontSize: 10, textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {plan.groups.map((g, i) => (
                  <tr key={i}>
                    <td style={{ padding: "7px 8px", fontFamily: FONT_UI, fontSize: 12.5, color: T.text, whiteSpace: "nowrap", position: "sticky", left: 0, background: T.bgSurface }}>
                      {g.name}{g.department ? ` · ${g.department}` : ""}{g.location ? ` · ${g.location}` : ""}
                    </td>
                    {g.monthly.map((v, n) => (
                      <td key={n} style={{ padding: "7px 8px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 12, color: T.textMuted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{money(v, cur)}</td>
                    ))}
                    <td style={{ padding: "7px 8px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 12.5, color: T.text, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{money(g.total, cur)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: "9px 8px", fontFamily: FONT_UI, fontSize: 12.5, fontWeight: 700, color: T.text, borderTop: `1px solid ${T.border}`, position: "sticky", left: 0, background: T.bgSurface }}>Monthly total</td>
                  {plan.totalsByMonth.map((v, n) => (
                    <td key={n} style={{ padding: "9px 8px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 12, fontWeight: 600, color: T.textBody, borderTop: `1px solid ${T.border}`, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{money(v, cur)}</td>
                  ))}
                  <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 14, fontWeight: 700, color: TOKENS.gold, borderTop: `1px solid ${T.border}`, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{money(plan.grandTotal, cur)}</td>
                </tr>
                <tr>
                  <td style={{ padding: "6px 8px", fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, position: "sticky", left: 0, background: T.bgSurface }}>Running total</td>
                  {plan.cumulative.map((v, n) => (
                    <td key={n} style={{ padding: "6px 8px", textAlign: "right", fontFamily: FONT_MONO, fontSize: 11.5, color: T.textMuted, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{money(v, cur)}</td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── TAB 3 — YOUR DOCUMENTS ───────────────────────────────────
function YourDocuments({ T, card, label, input, btn }) {
  const [docs, setDocs]   = useState([]);
  const [busy, setBusy]   = useState("");
  const [error, setError] = useState("");
  const [meta, setMeta]   = useState({ kind: "proposal", funder: "", sector: "" });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res  = await apiFetch(`${API}/library`);
      const json = await res.json();
      if (json.success) setDocs(json.documents || []);
    } catch { /* empty list is a fine failure mode here */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    for (const file of files) {
      setBusy(`Adding ${file.name}…`); setError("");
      try {
        const n = file.name.toLowerCase();
        let text = "";
        if (n.endsWith(".pdf")) text = await parsePDF(file);
        else if (n.endsWith(".docx")) text = await parseDocx(file);
        else if (n.endsWith(".xlsx") || n.endsWith(".xls")) text = await parseExcel(file);
        else { setError(`${file.name}: use PDF, Word or Excel`); continue; }
        if (!text.trim()) { setError(`${file.name}: no readable text found`); continue; }

        const res = await apiFetch(`${API}/library`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: file.name.replace(/\.[^.]+$/, ""), sourceName: file.name, text, ...meta }),
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error || "Could not save it");
      } catch (err) { setError(`${file.name}: ${err.message}`); }
    }
    setBusy(""); load();
  };

  const remove = async (id) => {
    await apiFetch(`${API}/library/${id}`, { method: "DELETE" }).catch(() => {});
    load();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={card}>
        <div style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textBody, lineHeight: 1.65, marginBottom: 4 }}>
          Add proposals and budgets you've written before. NofoDesk reads them and matches your wording, your structure and your evidence when it drafts something new — so what it writes sounds like your organisation, not like generic AI.
        </div>
        <div style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
          The more you add, the closer it gets. Your documents are private to your account and are never used to train any AI model.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 14 }}>
          <div>
            <span style={label}>What is it?</span>
            <select style={input} value={meta.kind} onChange={e => setMeta(m => ({ ...m, kind: e.target.value }))}>
              <option value="proposal">A proposal</option>
              <option value="budget">A budget</option>
              <option value="loi">A letter of intent</option>
              <option value="report">A donor report</option>
              <option value="template">A funder template</option>
              <option value="other">Something else</option>
            </select>
          </div>
          <div><span style={label}>Which funder? (optional)</span><input style={input} value={meta.funder} onChange={e => setMeta(m => ({ ...m, funder: e.target.value }))} placeholder="e.g. Global Fund" /></div>
          <div><span style={label}>Which sector? (optional)</span><input style={input} value={meta.sector} onChange={e => setMeta(m => ({ ...m, sector: e.target.value }))} placeholder="e.g. Health" /></div>
        </div>

        <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx,.xls" multiple onChange={onFiles} style={{ display: "none" }} />
        <button onClick={() => fileRef.current?.click()} style={btn(true)}>＋ Add documents</button>
        {busy && <span style={{ marginLeft: 12, fontFamily: FONT_UI, fontSize: 13, color: T.textMuted }}>{busy}</span>}
        {error && <div style={{ marginTop: 10, fontFamily: FONT_UI, fontSize: 13, color: TOKENS.statusRed }}>{error}</div>}
      </div>

      <div style={card}>
        <span style={label}>In your library ({docs.length})</span>
        {docs.length === 0 ? (
          <div style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textMuted, padding: "10px 0" }}>
            Nothing yet. Add a proposal you're proud of — that's the fastest way to improve what NofoDesk writes for you.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {docs.map(d => (
              <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.borderDim}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                    {d.kind}{d.funder ? ` · ${d.funder}` : ""}{d.sector ? ` · ${d.sector}` : ""} · {Math.round((d.chars || 0) / 1000)}k chars
                  </div>
                </div>
                <button onClick={() => remove(d.id)} aria-label={`Remove ${d.title}`} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 16, padding: 4 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
